import { useEffect, useRef } from 'react';
import { create } from 'zustand';

/**
 * Éditeurs de contenu (block/item/entity) ayant des modifications NON ENREGISTRÉES.
 * Chaque éditeur "sale" s'y inscrit avec sa fonction save(), pour permettre un
 * « Enregistrer tout » global depuis la barre en bas de l'écran.
 *
 * On garde ici la fonction save() de l'éditeur (et non une copie des données) :
 * le catalogue garde l'éditeur monté tant qu'il est sale, donc sa save()
 * (qui gère aussi les fichiers en attente) reste valide.
 */
export interface UnsavedDraft {
  key: string;
  kind: string;
  /** Sous-éditeur d'une même entrée : 'main' | 'drop' | 'recipe' (clés distinctes, pas d'écrasement). */
  scope: string;
  groupId: string;
  identifier: string;
  label: string;
  /** Snapshot des valeurs éditées (pour restaurer l'éditeur au retour). Optionnel. */
  payload?: unknown;
  save: () => Promise<boolean>;
}

interface UnsavedState {
  drafts: Record<string, UnsavedDraft>;
  register: (d: UnsavedDraft) => void;
  unregister: (key: string) => void;
}

export const useUnsavedStore = create<UnsavedState>((set) => ({
  drafts: {},
  register: (d) => set((s) => ({ drafts: { ...s.drafts, [d.key]: d } })),
  unregister: (key) =>
    set((s) => {
      if (!s.drafts[key]) return s;
      const next = { ...s.drafts };
      delete next[key];
      return { drafts: next };
    }),
}));

export function draftKey(kind: string, groupId: string, identifier: string, scope = 'main'): string {
  return `${kind}::${scope}::${groupId}::${identifier}`;
}

/**
 * Inscrit l'éditeur courant comme brouillon tant que `dirty` est vrai.
 * `save` peut changer à chaque rendu (closure) : on garde toujours la dernière via un ref.
 */
export function useUnsavedDraft(opts: {
  kind: string;
  scope?: string;
  groupId: string;
  identifier: string;
  label: string;
  dirty: boolean;
  save: () => Promise<boolean>;
}) {
  const register = useUnsavedStore((s) => s.register);
  const unregister = useUnsavedStore((s) => s.unregister);
  const scope = opts.scope ?? 'main';
  const key = draftKey(opts.kind, opts.groupId, opts.identifier, scope);
  const saveRef = useRef(opts.save);
  saveRef.current = opts.save;

  useEffect(() => {
    if (opts.dirty) {
      register({
        key,
        kind: opts.kind,
        scope,
        groupId: opts.groupId,
        identifier: opts.identifier,
        label: opts.label,
        save: () => saveRef.current(),
      });
    } else {
      unregister(key);
    }
  }, [opts.dirty, key, opts.kind, scope, opts.groupId, opts.identifier, opts.label, register, unregister]);

  // À la disparition de l'éditeur (navigation vers une autre page), on retire le brouillon.
  useEffect(() => () => unregister(key), [key, unregister]);
}

/** Lecture non réactive du snapshot d'un brouillon (pour restaurer l'éditeur au montage). */
export function getDraftPayload(key: string): unknown {
  return useUnsavedStore.getState().drafts[key]?.payload;
}

/**
 * Variante « snapshot » pour les éditeurs qui se démontent en changeant de sélection
 * (onglet grade, container, fichier) et dont une même instance sert plusieurs clés.
 * - Le brouillon SURVIT au démontage (pas de désinscription en cleanup) → reste dans la barre.
 * - À chaque changement de valeurs, on ré-inscrit un save() figé sur CES valeurs/CETTE clé,
 *   donc changer de clé n'écrase jamais le save d'une autre clé.
 * - `save()` se désinscrit lui-même en cas de succès.
 * Restaure l'état via {@link getDraftPayload} au montage côté composant.
 */
export function useSnapshotDraft(opts: {
  kind: string;
  scope?: string;
  groupId: string;
  identifier: string;
  label: string;
  dirty: boolean;
  payload: unknown;
  save: () => Promise<boolean>;
}) {
  const register = useUnsavedStore((s) => s.register);
  const unregister = useUnsavedStore((s) => s.unregister);
  const scope = opts.scope ?? 'main';
  const key = draftKey(opts.kind, opts.groupId, opts.identifier, scope);
  const saveRef = useRef(opts.save);
  saveRef.current = opts.save;
  const payloadJson = JSON.stringify(opts.payload ?? null);

  useEffect(() => {
    if (opts.dirty) {
      const snapshotSave = saveRef.current; // fige le closure courant (valeurs + clé de CE rendu)
      register({
        key,
        kind: opts.kind,
        scope,
        groupId: opts.groupId,
        identifier: opts.identifier,
        label: opts.label,
        payload: opts.payload,
        save: async () => {
          const ok = await snapshotSave();
          if (ok) unregister(key);
          return ok;
        },
      });
    } else {
      unregister(key);
    }
    // Pas de cleanup de désinscription : le brouillon persiste quand l'éditeur se démonte.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, opts.dirty, payloadJson, opts.label]);
}
